import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ShoppingBag, Minus, Plus, ArrowLeft, Clock, CheckCircle, CalendarClock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useCart } from '@/contexts/CartContext';
import { productsAPI } from '@/lib/api';
import { toast } from '@/components/ui/sonner';
import { CartDrawer } from '@/components/CartDrawer';
import { formatWindowDate } from '@/lib/windowDate';
import { money } from '@/lib/format';
import { formatPack } from '@/lib/units';

export default function ProductPage() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const { addItem } = useCart();

  const moq = Number(product?.moq || 0);

  useEffect(() => {
    productsAPI.get(id)
      .then(r => {
        setProduct(r.data);
        // Default the starting quantity to the MOQ so customers meet it.
        const m = Number(r.data?.moq || 0);
        if (m > 1) setQuantity(m);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  const upcoming = product?.window_state === 'upcoming';
  const windowOpen = product?.window_open !== false && !upcoming;
  const opensOn = formatWindowDate(product?.window_open_date);
  const decMin = moq > 1 ? moq : 1;

  const handleAddToCart = () => {
    if (!product) return;
    if (upcoming) {
      toast.info(opensOn ? `Opens on ${opensOn} — you can order then` : 'This vendor isn’t taking orders yet');
      return;
    }
    if (!windowOpen) {
      toast.error(`Order window for ${product.supplier_name || 'this supplier'} is closed`);
      return;
    }
    if (moq > 1 && quantity < moq) {
      toast.error(`Minimum order for ${product.name} is ${moq} units`);
      return;
    }
    const result = addItem(product, quantity);
    if (result?.conflict) return;
    toast.success(`${product.name} added to cart`);
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div className="aspect-[4/5] rounded-2xl bg-cream animate-pulse" />
          <div className="space-y-4">
            <div className="h-4 bg-cream rounded w-1/4" />
            <div className="h-8 bg-cream rounded w-3/4" />
            <div className="h-4 bg-cream rounded w-full" />
            <div className="h-4 bg-cream rounded w-2/3" />
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
        <h2 className="font-heading text-3xl text-foreground">Product not found</h2>
        <Link to="/shop" className="font-body text-vintage hover:underline mt-4 inline-block">Back to shop</Link>
      </div>
    );
  }

  return (
    <div data-testid="product-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex gap-8 items-start">
        <div className="flex-1 min-w-0">
      <Link
        to="/shop"
        data-testid="back-to-shop"
        className="inline-flex items-center gap-2 font-body text-sm text-foreground/70 hover:text-vintage mb-8 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" /> Back to shop
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
        {/* Image */}
        <div className="aspect-[4/5] rounded-3xl overflow-hidden bg-background">
          <img
            src={product.image_url}
            alt={product.name}
            className="w-full h-full object-cover"
            data-testid="product-image"
          />
        </div>

        {/* Details */}
        <div className="flex flex-col justify-center py-8">
          <Badge
            className="w-fit mb-4 bg-peach/25 text-foreground border-peach/30 rounded-full px-3 font-body text-xs uppercase tracking-widest"
            variant="outline"
          >
            {product.category_name}
          </Badge>

          <h1
            data-testid="product-name"
            className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light text-foreground tracking-tight leading-tight mb-4"
          >
            {product.name}
          </h1>

          <div className="flex items-center gap-3 mb-6">
            <span className="inline-block h-[3px] w-8 rounded-full bg-terracotta/60" />
            <p className="font-heading text-3xl font-medium text-foreground" data-testid="product-price">
              R{money(product.price)}
            </p>
          </div>

          {product.supplier_name && (
            <p className="font-body text-sm text-foreground/60 mb-2">
              Supplier: <span className="font-semibold text-foreground/70">{product.supplier_name}</span>
            </p>
          )}

          {formatPack(product) && (
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-teal/8 text-teal-dark text-sm font-body font-medium mb-3" data-testid="product-unit-size">
              {formatPack(product)}
            </div>
          )}

          {/* Window Status */}
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-body font-semibold mb-4 ${
            upcoming
              ? 'bg-gold/15 text-gold border border-gold/40'
              : windowOpen
              ? 'bg-teal/10 text-teal border border-teal/30'
              : 'bg-red-50 text-red-700 border border-red-200'
          }`} data-testid="window-status-badge">
            {upcoming ? <CalendarClock className="h-3.5 w-3.5" /> : windowOpen ? <CheckCircle className="h-3.5 w-3.5" /> : <Clock className="h-3.5 w-3.5" />}
            {upcoming ? (opensOn ? `Opening Soon · Opens ${opensOn}` : 'Opening Soon') : windowOpen ? 'Now Taking Orders' : 'Orders Closed'}
          </div>

          <p className="font-body text-base text-foreground/70 leading-relaxed mb-8" data-testid="product-description">
            {product.description}
          </p>

          {/* Quantity + Add to Cart */}
          <div className="flex items-center gap-4 mt-4">
            <div className="flex items-center border border-border rounded-full">
              <Button
                variant="ghost"
                size="icon"
                data-testid="qty-decrease"
                onClick={() => setQuantity(q => Math.max(decMin, q - 1))}
                disabled={quantity <= decMin}
                className="h-10 w-10 rounded-full hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Minus className="h-4 w-4" />
              </Button>
              <span className="font-body text-sm font-medium w-8 text-center" data-testid="qty-display">{quantity}</span>
              <Button
                variant="ghost"
                size="icon"
                data-testid="qty-increase"
                onClick={() => setQuantity(q => q + 1)}
                className="h-10 w-10 rounded-full hover:bg-muted"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <Button
              data-testid="add-to-cart-btn"
              variant="ctaPrimary"
              onClick={handleAddToCart}
              disabled={!windowOpen}
              title={upcoming && opensOn ? `Opens on ${opensOn}` : undefined}
              className="rounded-full px-8 py-6 text-sm font-body font-medium flex-1 max-w-xs transition-all duration-200 hover:-translate-y-0.5 shadow-md gap-2 disabled:opacity-50"
            >
              {upcoming ? <CalendarClock className="h-4 w-4" /> : <ShoppingBag className="h-4 w-4" />}
              {upcoming ? (opensOn ? `Opens ${opensOn}` : 'Opening soon') : !windowOpen ? 'Orders Closed' : 'Add to Cart'}
            </Button>
          </div>
          {moq > 1 && (
            <p className="font-body text-xs text-foreground/60 mt-3" data-testid="product-moq-note">
              Minimum order quantity for this product is <strong className="text-foreground">{moq} units</strong>.
            </p>
          )}
        </div>
      </div>
        </div>
        <CartDrawer />
      </div>
    </div>
  );
}
